import React, { useEffect, useState, useMemo } from "react";
import { Search, Download, Shield } from "lucide-react";
import dayjs from "dayjs";

const API_BASE = process.env.REACT_APP_API_BASE || "http://localhost:8000";
const card = "rounded-xl border border-[#00ff90]/30 bg-[#0d0d0d] shadow-[0_0_12px_#00ff9022]";
const inputCls = "bg-black border border-[#00ff90]/20 rounded-lg px-3 py-2 text-sm text-white outline-none focus:border-[#00ff90]/50 transition-colors";
const labelCls = "text-xs text-[#00ff90]/50 uppercase tracking-wider block mb-1.5";

const ACTION_STYLE = {
  approve:         "text-[#00ff90] bg-[#00ff90]/10 border-[#00ff90]/20",
  reject:          "text-red-400 bg-red-500/10 border-red-500/20",
  override:        "text-orange-400 bg-orange-500/10 border-orange-500/20",
  balance_adjust:  "text-blue-400 bg-blue-500/10 border-blue-500/20",
  bulk_credit:     "text-purple-400 bg-purple-500/10 border-purple-500/20",
  carry_over:      "text-cyan-400 bg-cyan-500/10 border-cyan-500/20",
  lapse:           "text-yellow-400 bg-yellow-500/10 border-yellow-500/20",
  encashment:      "text-pink-400 bg-pink-500/10 border-pink-500/20",
  policy_create:   "text-white/50 bg-white/5 border-white/10",
  policy_update:   "text-white/50 bg-white/5 border-white/10",
  policy_delete:   "text-red-400/60 bg-red-500/8 border-red-500/15",
  add_on_behalf:   "text-[#00ff90]/60 bg-[#00ff90]/8 border-[#00ff90]/15",
  cancel:          "text-white/30 bg-white/5 border-white/10",
};

export default function AuditLogTab() {
  const [logs,    setLogs]    = useState([]);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);
  const [page,    setPage]    = useState(1);
  const [total,   setTotal]   = useState(0);
  const [exporting, setExporting] = useState(false);

  const [filters, setFilters] = useState({
    search: "", action: "ALL", admin_id: "", employee_id: "",
    from: "", to: "",
  });

  const token = localStorage.getItem("token");
  const PER_PAGE = 50;

  const load = async (p = 1) => {
    setLoading(true); setError(null);
    try {
      const qs = new URLSearchParams({
        page: p, per_page: PER_PAGE,
        ...(filters.action     !== "ALL" && { action:      filters.action      }),
        ...(filters.admin_id               && { admin_id:    filters.admin_id    }),
        ...(filters.employee_id            && { employee_id: filters.employee_id }),
        ...(filters.from                   && { from:        filters.from        }),
        ...(filters.to                     && { to:          filters.to          }),
      }).toString();
      const res = await fetch(`${API_BASE}/api/admin/leave-rules/audit-log?${qs}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setLogs(Array.isArray(data) ? data : data?.items || []);
      setTotal(data?.total || 0);
      setPage(p);
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(1); }, [filters]);

  const exportLog = async () => {
    setExporting(true);
    try {
      const qs = new URLSearchParams({ ...filters, format: "csv" }).toString();
      const res = await fetch(`${API_BASE}/api/admin/leave-rules/audit-log/export?${qs}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement("a");
      a.href = url; a.download = `leave_audit_${dayjs().format("YYYY-MM-DD")}.csv`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { setError(e.message); }
    finally { setExporting(false); }
  };

  const visible = useMemo(() => {
    if (!filters.search) return logs;
    const q = filters.search.toLowerCase();
    return logs.filter(l =>
      (l.admin_name     || "").toLowerCase().includes(q) ||
      (l.employee_name  || "").toLowerCase().includes(q) ||
      (l.employee_id    || "").toLowerCase().includes(q) ||
      (l.action         || "").toLowerCase().includes(q) ||
      (l.note           || "").toLowerCase().includes(q)
    );
  }, [logs, filters.search]);

  const totalPages = Math.ceil(total / PER_PAGE);

  return (
    <div className="space-y-4">

      {/* Filters */}
      <div className={`${card} p-4`}>
        <div className="flex flex-wrap gap-3">
          <div className="flex items-center gap-2 flex-1 min-w-[160px]">
            <Search size={13} className="text-[#00ff90]/40 shrink-0" />
            <input className={`${inputCls} flex-1`} placeholder="Search admin, employee, action, note…"
              value={filters.search} onChange={e => setFilters(f => ({ ...f, search: e.target.value }))} />
          </div>
          <select className={inputCls} value={filters.action} onChange={e => setFilters(f => ({ ...f, action: e.target.value }))}>
            <option value="ALL">All Actions</option>
            {["approve","reject","override","balance_adjust","bulk_credit","carry_over","lapse","encashment","policy_create","policy_update","policy_delete","add_on_behalf","cancel"].map(a => (
              <option key={a} value={a}>{a.replace(/_/g, " ")}</option>
            ))}
          </select>
          <input className={inputCls} placeholder="Admin ID…" value={filters.admin_id}
            onChange={e => setFilters(f => ({ ...f, admin_id: e.target.value }))} style={{ width: 120 }} />
          <input className={inputCls} placeholder="Employee ID…" value={filters.employee_id}
            onChange={e => setFilters(f => ({ ...f, employee_id: e.target.value }))} style={{ width: 120 }} />
          <input type="date" className={inputCls} value={filters.from} onChange={e => setFilters(f => ({ ...f, from: e.target.value }))} />
          <input type="date" className={inputCls} value={filters.to}   onChange={e => setFilters(f => ({ ...f, to:   e.target.value }))} />
          <button onClick={exportLog} disabled={exporting}
            className="flex items-center gap-1.5 text-xs px-3 py-2 rounded-lg border border-white/10 text-white/40 hover:text-white transition-colors">
            <Download size={12} /> Export CSV
          </button>
        </div>
      </div>

      {error && <div className="rounded-xl border border-red-500/30 bg-red-500/10 p-4 text-red-300 text-sm">⚠ {error}</div>}

      {/* Log table */}
      <div className={card}>
        <div className="px-5 py-4 border-b border-white/5 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Shield size={13} className="text-[#00ff90]/60" />
            <span className="text-xs text-[#00ff90]/60 uppercase tracking-widest font-medium">Audit Log</span>
          </div>
          <span className="text-xs text-white/30">{total} entries</span>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-white/5">
                {["Timestamp","Admin","Action","Employee","Details","Note"].map(h => (
                  <th key={h} className="px-3 py-3 text-left text-xs text-[#00ff90]/40 uppercase tracking-wider font-medium whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                Array.from({ length: 8 }).map((_, i) => (
                  <tr key={i}><td colSpan={6} className="px-4 py-3"><div className="h-8 rounded bg-white/5 animate-pulse" /></td></tr>
                ))
              ) : visible.length === 0 ? (
                <tr><td colSpan={6} className="px-4 py-8 text-center text-white/20 italic text-sm">No audit entries found.</td></tr>
              ) : visible.map((l, i) => {
                const actionStyle = ACTION_STYLE[l.action] || "text-white/40 bg-white/5 border-white/10";
                return (
                  <tr key={i} className="border-b border-white/5 hover:bg-white/3 transition-colors">
                    <td className="px-3 py-2.5 text-white/30 text-xs whitespace-nowrap">
                      {l.created_at ? dayjs(l.created_at).format("DD MMM YYYY HH:mm") : "—"}
                    </td>
                    <td className="px-3 py-2.5">
                      <p className="text-xs text-white/60">{l.admin_name || "—"}</p>
                      <p className="text-xs text-white/25">{l.admin_id}</p>
                    </td>
                    <td className="px-3 py-2.5">
                      <span className={`text-xs px-2 py-0.5 rounded-full border capitalize ${actionStyle}`}>
                        {(l.action || "—").replace(/_/g, " ")}
                      </span>
                    </td>
                    <td className="px-3 py-2.5">
                      {l.employee_name ? (
                        <>
                          <p className="text-xs text-white/60">{l.employee_name}</p>
                          <p className="text-xs text-white/25">{l.employee_id}</p>
                        </>
                      ) : <span className="text-white/20 text-xs">—</span>}
                    </td>
                    <td className="px-3 py-2.5 text-white/40 text-xs max-w-[200px] truncate">
                      {l.details || "—"}
                    </td>
                    <td className="px-3 py-2.5 text-white/30 text-xs max-w-[150px] truncate">
                      {l.note || "—"}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="px-5 py-3 border-t border-white/5 flex items-center justify-between">
            <span className="text-xs text-white/30">Page {page} of {totalPages}</span>
            <div className="flex gap-2">
              <button onClick={() => load(page - 1)} disabled={page <= 1 || loading}
                className="text-xs px-3 py-1.5 rounded-lg border border-white/10 text-white/40 hover:text-white disabled:opacity-30 transition-colors">
                ← Prev
              </button>
              <button onClick={() => load(page + 1)} disabled={page >= totalPages || loading}
                className="text-xs px-3 py-1.5 rounded-lg border border-white/10 text-white/40 hover:text-white disabled:opacity-30 transition-colors">
                Next →
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}